/**
 * TypeScript interfaces for Home Intelligence System
 * Ported from iOS Swift models for centralized backend calculations
 */

// MARK: - Core Intelligence Data Models

export interface HomeIntelligenceData {
  propertyScore: PropertyIntelligenceScore;
  marketAnalytics: MarketAnalytics | null;
  propertyAssessment: PropertyAssessment | null;
  quickInsights: QuickInsight[];
  dataQuality: DataQualityInfo;
  lastUpdated: string; // ISO 8601 date string
}

export interface PropertyIntelligenceScore {
  overall: number; // 0-100
  marketPosition: number;
  propertyCondition: number;
  investmentPotential: number;
  riskFactors: number;
  marketMomentum: number;
}

export enum ScoreCategory {
  POOR = 'Poor',
  FAIR = 'Fair',
  GOOD = 'Good',
  EXCELLENT = 'Excellent',
  OUTSTANDING = 'Outstanding',
  UNKNOWN = 'Unknown'
}

export enum MarketPosition {
  UNDERVALUED = 'Undervalued',
  FAIRLY_PRICED = 'Fairly Priced',
  OVERVALUED = 'Overvalued',
  UNKNOWN = 'Unknown'
}

export interface MarketAnalytics {
  currentValue: number | null;
  listPrice: number | null;
  pricePerSqFt: number | null;
  daysOnMarket: number | null;
  marketPosition: MarketPosition | null;
  priceComparison: PriceComparison | null;
  lastSaleDate: string | null; // ISO 8601 date string
}

export interface PriceComparison {
  vsListPrice: number | null; // Percentage difference
  marketTrend: string | null; // "above", "below", "at"
  confidenceLevel: number | null; // 0-1
}

export interface PropertyAssessment {
  yearBuilt: number | null;
  totalSqFt: number | null;
  lotSize: number | null;
  propertyType: string | null;
  taxAssessment: TaxAssessmentInfo | null;
  propertyFeatures: PropertyFeaturesInfo | null;
}

export interface TaxAssessmentInfo {
  assessedValue: number | null;
  annualTax: number | null;
  taxRate: number | null;
  lastAssessmentYear: number | null;
}

export interface PropertyFeaturesInfo {
  bedrooms: number | null;
  bathrooms: number | null;
  hasGarage: boolean | null;
  hasFireplace: boolean | null;
  hvacType: string | null;
  foundationType: string | null;
}

export enum InsightType {
  POSITIVE = 'positive',
  NEUTRAL = 'neutral',
  CAUTION = 'caution',
  NEGATIVE = 'negative'
}

export enum ImportanceLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface QuickInsight {
  id: string;
  title: string;
  description: string;
  type: InsightType;
  importance: ImportanceLevel;
}

export interface DataQualityInfo {
  hasMarketData: boolean;
  hasPropertyData: boolean;
  dataCompleteness: number; // 0-1
  lastMarketUpdate: string | null; // ISO 8601 date string
  lastPropertyUpdate: string | null; // ISO 8601 date string
}

// MARK: - Input Data Models (from Rentcast API)

export interface PropertyInformation {
  id: string;
  formattedAddress: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
  county: string | null;
  latitude: number | null;
  longitude: number | null;
  propertyType: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFootage: number | null;
  lotSize: number | null;
  yearBuilt: number | null;
  assessorID: string | null;
  legalDescription: string | null;
  subdivision: string | null;
  zoning: string | null;
  lastSaleDate: string | null;
  lastSalePrice: number | null;
  currentListingPrice: number | null;
  hoa: HOAInfo | null;
  features: PropertyFeatures | null;
  taxAssessments: Record<string, TaxAssessment> | null;
  propertyTaxes: Record<string, PropertyTax> | null;
  history: Record<string, PropertyHistoryEvent> | null;
  owner: PropertyOwner | null;
  ownerOccupied: boolean | null;
}

export interface PropertyFeatures {
  architectureType: string | null;
  cooling: boolean | null;
  coolingType: string | null;
  exteriorType: string | null;
  fireplace: boolean | null;
  floorCount: number | null;
  garage: boolean | null;
  garageSpaces: number | null;
  garageType: string | null;
  heating: boolean | null;
  heatingType: string | null;
  roofType: string | null;
  roomCount: number | null;
  unitCount: number | null;
}

export interface TaxAssessment {
  year: number;
  value: number | null;
  land: number | null;
  improvements: number | null;
}

export interface PropertyTax {
  year: number;
  total: number | null;
}

export interface PropertyHistoryEvent {
  event: string;
  date: string;
  price: number | null;
}

export interface PropertyOwner {
  names: string[] | null;
  type: string | null;
  mailingAddress: PropertyAddress | null;
}

export interface PropertyAddress {
  id: string | null;
  formattedAddress: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

export interface HOAInfo {
  fee: number | null;
}

/**
 * Sale Listing Property data from Rentcast API
 *
 * IMPORTANT: All fields are nullable because we don't control the Rentcast API response.
 * While fields like id, bedrooms, bathrooms, squareFootage, and price are CRITICAL for
 * the application to function, Rentcast may not always provide them (unlikely but possible).
 *
 * The nullable types accurately model the uncertain external API response.
 * Use type guards (see shared/intelligence/typeGuards.ts) to validate data before use.
 * Invalid/incomplete listings are rejected by validation and not cached or displayed.
 */
export interface SaleListingProperty {
  id: string | null;
  address: string | null;
  formattedAddress: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  stateFips: string | null;
  zipCode: string | null;
  county: string | null;
  countyFips: string | null;
  latitude: number | null;
  longitude: number | null;
  bedrooms: number | null;              // Critical field - validated by type guard
  bathrooms: number | null;             // Critical field - validated by type guard
  squareFootage: number | null;         // Critical field - validated by type guard
  lotSize: number | null;
  price: number | null;                 // Critical field - validated by type guard
  listedDate: string | null; // ISO 8601 date string (renamed from listDate)
  removedDate: string | null; // ISO 8601 date string
  status: string | null; // e.g., "Active", "Inactive"
  propertyType: string | null;
  yearBuilt: number | null;
  daysOnMarket: number | null;
  pricePerSquareFoot: number | null;
}

// MARK: - API Response Models

export interface PropertyResponse {
  success: boolean;
  data: PropertyInformation[];
}

export interface SaleListingsResponse {
  success: boolean;
  data: SaleListingProperty[] | null;
  error: string | null;
}

export interface PropertyPublicDataResponse {
  success: boolean;
  data: PropertyInformation[] | null;
  cached: boolean;
  error?: string;
  details?: string;
}

// MARK: - Error Types

export enum HomeIntelligenceErrorType {
  NO_HOME_ID = 'no_home_id',
  NETWORK_ERROR = 'network_error',
  DATA_SERVICE_ERROR = 'data_service_error',
  INSUFFICIENT_DATA = 'insufficient_data',
  CALCULATION_ERROR = 'calculation_error',
  CACHE_ERROR = 'cache_error',
  INVALID_DATA = 'invalid_data',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  TIMEOUT = 'timeout'
}

export interface HomeIntelligenceError {
  type: HomeIntelligenceErrorType;
  message: string;
  details?: any;
}

// MARK: - Intelligence Calculation Functions

export interface IntelligenceCalculationResult {
  intelligence: HomeIntelligenceData;
  error?: HomeIntelligenceError;
}

// MARK: - Utility Types

export interface ScoreCategoryInfo {
  category: ScoreCategory;
  color: string;
  description: string;
}

export interface IntelligenceLoadingState {
  isLoading: boolean;
  data: HomeIntelligenceData | null;
  error: HomeIntelligenceError | null;
}

// Helper functions for score categorization
export function getScoreCategory(score: number): ScoreCategory {
  if (score < 30) return ScoreCategory.POOR;
  if (score < 50) return ScoreCategory.FAIR;
  if (score < 70) return ScoreCategory.GOOD;
  if (score < 85) return ScoreCategory.EXCELLENT;
  if (score <= 100) return ScoreCategory.OUTSTANDING;
  return ScoreCategory.UNKNOWN;
}

export function getScoreCategoryInfo(score: number): ScoreCategoryInfo {
  const category = getScoreCategory(score);

  const categoryMap: Record<ScoreCategory, { color: string; description: string }> = {
    [ScoreCategory.POOR]: {
      color: '#ef4444',
      description: 'Significant concerns identified'
    },
    [ScoreCategory.FAIR]: {
      color: '#f97316',
      description: 'Some areas need attention'
    },
    [ScoreCategory.GOOD]: {
      color: '#3b82f6',
      description: 'Solid property with good potential'
    },
    [ScoreCategory.EXCELLENT]: {
      color: '#10b981',
      description: 'Strong investment opportunity'
    },
    [ScoreCategory.OUTSTANDING]: {
      color: '#8b5cf6',
      description: 'Exceptional property opportunity'
    },
    [ScoreCategory.UNKNOWN]: {
      color: '#6b7280',
      description: 'Insufficient data for analysis'
    }
  };

  return {
    category,
    ...categoryMap[category]
  };
}

export function getDataQualityDescription(completeness: number): string {
  if (completeness < 0.3) return 'Limited data available';
  if (completeness < 0.6) return 'Partial data available';
  if (completeness < 0.8) return 'Good data coverage';
  if (completeness <= 1.0) return 'Comprehensive data';
  return 'Unknown data quality';
}

export function hasMinimumData(dataQuality: DataQualityInfo): boolean {
  return dataQuality.hasMarketData || dataQuality.hasPropertyData;
}